import { Injectable, OnDestroy } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  ResolveEnd,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * Drives the top-of-page progress bar (app-progress) from what is actually
 * loading, instead of a canned animation:
 *
 *   phase        range      driven by
 *   ─────────────────────────────────────────────────────────────────────
 *   navigating   10 → 60    RouteConfigLoadStart/End (lazy chunk download),
 *                           ResolveEnd
 *   requests     60 → 90    HTTP requests counted by RequestInterceptor —
 *                           advanced by completed / total, so 4 calls with
 *                           1 back = 67.5, not a guess
 *   done         100        NavigationEnd (or Cancel/Error) AND no pending
 *                           requests → snap, fade, reset
 *
 * Between real events the bar only creeps a little toward the phase ceiling
 * (never past it) so it never looks frozen, but it never claims to be nearly
 * done when it isn't.
 *
 * Only requests that start while navigating, or within REQUEST_GRACE_MS of
 * NavigationEnd (a page's ngOnInit fires its calls then), are counted.
 * Background traffic — the recent-order ticker, Retail AI chat, polling —
 * happens outside that window and never shows the bar.
 */
@Injectable({ providedIn: 'root' })
export class LoadingProgressService implements OnDestroy {
  /** 0–100, what the bar shows */
  readonly progress$ = new BehaviorSubject<number>(0);
  /** false while the bar is hidden / fading out */
  readonly visible$ = new BehaviorSubject<boolean>(false);

  private static readonly NAV_START = 10;
  private static readonly CHUNK_START = 20;
  private static readonly CHUNK_END = 60;
  private static readonly REQ_END = 90;
  private static readonly TICK_MS = 200;
  private static readonly REQUEST_GRACE_MS = 1000;
  private static readonly FADE_MS = 250;

  private navigating = false;
  private routeSettledAt = 0; // timestamp of NavigationEnd/Cancel/Error
  private trackedTotal = 0; // requests counted for this load
  private trackedPending = 0;
  private ceiling = LoadingProgressService.CHUNK_END;
  private tick: ReturnType<typeof setInterval> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private routerSub: Subscription;

  constructor(router: Router) {
    this.routerSub = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.begin();
      } else if (event instanceof RouteConfigLoadStart) {
        this.raiseTo(LoadingProgressService.CHUNK_START);
        this.ceiling = LoadingProgressService.CHUNK_END;
      } else if (event instanceof RouteConfigLoadEnd) {
        this.raiseTo(LoadingProgressService.CHUNK_END);
        this.ceiling = LoadingProgressService.REQ_END;
      } else if (event instanceof ResolveEnd) {
        this.raiseTo(LoadingProgressService.CHUNK_END);
        this.ceiling = LoadingProgressService.REQ_END;
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.navigating = false;
        this.routeSettledAt = Date.now();
        this.raiseTo(LoadingProgressService.CHUNK_END);
        this.ceiling = LoadingProgressService.REQ_END;
        // give the new page's ngOnInit a moment to fire its requests
        setTimeout(() => this.finishIfIdle(), 50);
      }
    });
  }

  /* ---------- called by RequestInterceptor ---------- */

  /** @returns true when the request is being counted (pass to endRequest) */
  beginRequest(): boolean {
    if (!this.shouldTrack()) return false;
    this.trackedTotal++;
    this.trackedPending++;
    this.applyRequestProgress();
    return true;
  }

  endRequest(tracked: boolean): void {
    if (!tracked) return;
    this.trackedPending = Math.max(0, this.trackedPending - 1);
    this.applyRequestProgress();
    this.finishIfIdle();
  }

  /* ---------- lifecycle ---------- */

  private begin(): void {
    this.clearFade();
    this.navigating = true;
    this.routeSettledAt = 0;
    this.trackedTotal = 0;
    this.trackedPending = 0;
    this.ceiling = LoadingProgressService.CHUNK_END;
    this.visible$.next(true);
    this.progress$.next(LoadingProgressService.NAV_START);
    if (!this.tick) {
      this.tick = setInterval(() => this.creep(), LoadingProgressService.TICK_MS);
    }
  }

  private shouldTrack(): boolean {
    if (this.navigating) return true;
    return (
      this.visible$.value &&
      this.routeSettledAt > 0 &&
      Date.now() - this.routeSettledAt < LoadingProgressService.REQUEST_GRACE_MS
    );
  }

  /** requests phase: 60 → 90 in proportion to completed / total */
  private applyRequestProgress(): void {
    if (this.navigating || !this.trackedTotal) return;
    const completed = this.trackedTotal - this.trackedPending;
    const span = LoadingProgressService.REQ_END - LoadingProgressService.CHUNK_END;
    this.raiseTo(
      LoadingProgressService.CHUNK_END + (span * completed) / this.trackedTotal
    );
  }

  private finishIfIdle(): void {
    if (this.navigating || this.trackedPending > 0 || !this.visible$.value) return;
    this.complete();
  }

  private complete(): void {
    this.stopTick();
    this.progress$.next(100);
    this.clearFade();
    this.fadeTimer = setTimeout(() => {
      this.visible$.next(false);
      // reset only after the fade so the bar never animates backwards
      this.fadeTimer = setTimeout(() => {
        if (!this.visible$.value) this.progress$.next(0);
      }, LoadingProgressService.FADE_MS);
    }, LoadingProgressService.FADE_MS);
  }

  /** small, decaying step toward the current ceiling — never reaches it */
  private creep(): void {
    const p = this.progress$.value;
    if (p >= this.ceiling - 0.5) return;
    this.progress$.next(p + (this.ceiling - p) * 0.06);
  }

  private raiseTo(value: number): void {
    if (value > this.progress$.value) this.progress$.next(value);
  }

  private stopTick(): void {
    if (this.tick) {
      clearInterval(this.tick);
      this.tick = null;
    }
  }

  private clearFade(): void {
    if (this.fadeTimer) {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.stopTick();
    this.clearFade();
    this.routerSub?.unsubscribe();
  }
}
